import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LogDetailSummary } from '@/components/knowledge/LogDetailSummary';
import { ProcessedTicketsTable } from '@/components/knowledge/ProcessedTicketsTable';
import { ErrorDetailSheet } from '@/components/knowledge/ErrorDetailSheet';
import { ConversationSheet } from '@/components/knowledge/ConversationSheet';
import { useExportLogCSV, useExtractionLogDetails, useSkippedConversation } from '@/hooks/useKnowledge';
import { ProcessedTicketDetail } from '@/types/knowledge';

export function KnowledgeExtractionLogDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/settings/knowledge-extraction/logs');
    }
  };
  const { data: log, isLoading, isError, refetch } = useExtractionLogDetails(id);
  const exportLogCsv = useExportLogCSV();

  const [errorTicket, setErrorTicket] = useState<ProcessedTicketDetail | null>(null);
  const [conversationTicket, setConversationTicket] = useState<ProcessedTicketDetail | null>(null);

  const skippedProtocol =
    conversationTicket?.status === 'skipped' && conversationTicket.skipReason !== 'duplicate_protocol'
      ? conversationTicket.protocol
      : '';

  const {
    data: skippedConversation,
    isLoading: isLoadingSkippedConversation,
    isError: isSkippedConversationError,
  } = useSkippedConversation(id, skippedProtocol, !!skippedProtocol && !!conversationTicket);

  if (isLoading) {
    return <div className="container mx-auto py-8 text-muted-foreground">Carregando detalhes do log...</div>;
  }

  if (isError) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <p className="text-destructive">Erro ao carregar detalhes do log</p>
        <Button variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <p className="text-muted-foreground">Log não encontrado</p>
        <Button variant="outline" onClick={handleBack}>Voltar</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 md:py-6 lg:py-8 px-4 md:px-6 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold">Log de Extração</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{format(new Date(log.runAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</span>
            <Badge variant={log.runType === 'manual' ? 'default' : 'secondary'}>
              {log.runType === 'manual' ? 'Manual' : 'Automatico'}
            </Badge>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={() => exportLogCsv.mutate(log.id)}
          disabled={exportLogCsv.isPending}
        >
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      <LogDetailSummary log={log} isLoading={false} />

      <ProcessedTicketsTable
        tickets={log.processedTickets}
        onViewError={setErrorTicket}
        onViewConversation={setConversationTicket}
        isLoading={false}
      />

      <ErrorDetailSheet
        isOpen={!!errorTicket}
        onClose={() => setErrorTicket(null)}
        ticket={errorTicket}
      />

      <ConversationSheet
        isOpen={!!conversationTicket}
        onClose={() => setConversationTicket(null)}
        protocol={conversationTicket?.protocol || ''}
        conversationText={
          conversationTicket?.status === 'skipped'
            ? skippedConversation?.conversationText
            : conversationTicket?.conversationText
        }
        isLoading={conversationTicket?.status === 'skipped' && isLoadingSkippedConversation}
        isError={conversationTicket?.status === 'skipped' && isSkippedConversationError}
      />
    </div>
  );
}
